import React, { useMemo } from "react";
import classes from "./subjectFormModal.module.css";
import mergeClassNames from "merge-class-names";
import LoadingButton from "../../../components/LoadingButton";
import { useSubjectFormDataSelector } from "../../../redux/selector";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../redux/store";
import {
  addSemesterStateSubject,
  deleteSemesterStateSubject,
  hideSubjectFormData,
  updateSemesterStateSubject,
} from "../../../redux/slices/gradeFileSlide";
import { Subject } from "../../../models/subject";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { Formik } from "formik";
import CustomInput from "../../../components/CustomInput";
import ReactModal from "react-modal";
import Modal from "react-modal";
import { useTranslation } from "react-i18next";

Modal.setAppElement("#root");

function SubjectFormModal() {
  const { visible, mode, subject, semesterId } = useSubjectFormDataSelector();
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();

  const validationSchema = useMemo(() => {
    return Yup.object().shape({
      name: Yup.string().required().label(t("gpa_cpa.subject_name")),
      credit: Yup.number()
        .integer()
        .min(0)
        .required()
        .label(t("gpa_cpa.credits")),
    });
  }, [t]);

  function handleSubmit({ name, credit }: { name: string; credit: string }) {
    const newSubjectInfo: Subject =
      mode === "edit"
        ? ({
            ...subject,
            name,
            credit: Number.parseInt(credit),
          } as any)
        : ({
            name,
            credit: Number.parseInt(credit),
          } as any);
    dispatch(
      mode === "edit"
        ? updateSemesterStateSubject({
            semesterId,
            subject: newSubjectInfo,
          })
        : addSemesterStateSubject({
            semesterId,
            subject: newSubjectInfo,
          })
    );
    toast.info(
      `${mode === "edit" ? t("common.update") : t("common.add")} ${t(
        "gpa_cpa.subject"
      )} ${t("common.successfully")}.`
    );
  }

  function handleDelete() {
    const res = window.confirm(
      t("common.delete_confirm", { label: t("gpa_cpa.subject") })
    );
    if (res) {
      dispatch(
        deleteSemesterStateSubject({
          semesterId,
          subject: subject as Subject,
        })
      );
      toast.info(
        `${t("common.deleted")} ${t("gpa_cpa.subject")} ${t(
          "common.successfully"
        )}`
      );
    }
  }

  function closeSubjectFormPopup() {
    dispatch(hideSubjectFormData());
  }

  function handleCancel() {
    closeSubjectFormPopup();
  }

  return (
    <ReactModal
      isOpen={visible}
      onRequestClose={handleCancel}
      shouldCloseOnOverlayClick={true}
      style={{
        content: {
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          padding: 0,
          width: "1000px",
          maxWidth: "95vw",
          height: "var(--modal-height)",
          maxHeight: "80vh",
        },
        overlay: {
          backgroundColor: "rgba(0, 0, 0, 0.4)",
          zIndex: 1000,
        },
      }}
    >
      <div
        className={mergeClassNames(
          classes.root,
          mode === "edit" ? classes.isEdit : classes.isAdd
        )}
      >
        <h2 className={classes.heading}>
          {mode === "edit"
            ? t("gpa_cpa.edit_subject")
            : t("gpa_cpa.add_subject")}
        </h2>
        <div className={classes.formContainer}>
          <Formik
            initialValues={{
              name: mode === "edit" && subject ? subject.name : "",
              credit:
                mode === "edit" && subject && subject.credit !== null
                  ? "" + subject.credit
                  : "",
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ handleSubmit }) => (
              <>
                <div className={classes.inputContainer}>
                  <CustomInput
                    fieldLabel={t("gpa_cpa.subject_name")}
                    fieldName={"name"}
                    placeholder={t("gpa_cpa.subject_name")}
                  />
                  <CustomInput
                    fieldLabel={t("gpa_cpa.credits")}
                    fieldName={"credit"}
                    placeholder={t("gpa_cpa.credits")}
                    type={"number"}
                  />
                </div>
                <div className={classes.buttonContainer}>
                  <LoadingButton
                    className={classes.importButton}
                    onClick={handleSubmit}
                  >
                    {mode === "edit" ? t("common.update") : t("common.add")}
                  </LoadingButton>
                  {mode === "edit" ? (
                    <LoadingButton
                      className={classes.cancelButton}
                      onClick={handleDelete}
                    >
                      {t("common.deleted")}
                    </LoadingButton>
                  ) : null}
                  <LoadingButton
                    className={classes.cancelButton}
                    onClick={handleCancel}
                  >
                    {t("common.cancel")}
                  </LoadingButton>
                </div>
              </>
            )}
          </Formik>
        </div>
      </div>
    </ReactModal>
  );
}

export default SubjectFormModal;
